import React, { useState } from 'react';
import { VolumeUpIcon, VolumeOffIcon, LockIcon } from './Icons';
import { sounds } from '../utils/audio';

export default function Navbar({ onOpenParentGate, activeSection, onNavigate }) {
  const [isMuted, setIsMuted] = useState(sounds.isMuted());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleAudioToggle = () => {
    const muted = sounds.toggleMute();
    setIsMuted(muted);
    if (!muted) {
      sounds.playBgMusic();
      sounds.playSound('tap');
    }
  };

  const navLinks = [
    { id: 'maths', label: 'Learn Maths', href: '#maths', badge: 'App' },
    { id: 'youtube', label: 'YouTube', href: '#youtube', badge: 'Videos' },
    { id: 'shop', label: 'Shop', href: '#shop', badge: 'New' },
    { id: 'playground', label: 'Playground', href: '#playground', highlight: true },
  ];

  const handleLinkClick = (link) => {
    sounds.playSound('tap');
    if (onNavigate) {
      onNavigate(link.id);
    }
    setMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b-2 border-kiki-sky/80 transition-all">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          
          {/* Brand Logo */}
          <a 
            href="#" 
            onClick={() => sounds.playSound('pop')}
            className="flex items-center gap-3 group"
          >
            <div className="relative w-12 h-12 rounded-2xl bg-gradient-to-tr from-kiki-yellow via-kiki-pink to-kiki-blue p-0.5 shadow-md group-hover:scale-105 group-hover:rotate-2 transition-transform">
              <img 
                src="/assets/images/logo.png" 
                alt="Kiki & Kids Logo" 
                className="w-full h-full object-contain rounded-2xl bg-white p-1"
              />
            </div>
            <div className="flex flex-col">
              <span className="font-heading font-black text-2xl tracking-tight text-slate-900 flex items-center gap-1">
                Kiki <span className="text-kiki-coral">&amp;</span> Kids
              </span>
              <span className="text-[11px] font-bold text-kiki-blue tracking-wider uppercase -mt-1">
                kikiandkids.com
              </span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
            {navLinks.map((link) => (
              <a
                key={link.id}
                href={link.href}
                onClick={() => handleLinkClick(link)}
                className={`px-3.5 py-2 rounded-full font-heading text-xs xl:text-sm font-bold transition-all flex items-center gap-1.5 ${
                  link.highlight
                    ? 'bg-kiki-yellow/30 text-amber-900 hover:bg-kiki-yellow/50 border border-kiki-yellow/60 shadow-xs'
                    : 'text-slate-700 hover:text-kiki-blue hover:bg-kiki-sky/50'
                }`}
              >
                <span>{link.label}</span>
                {link.badge && (
                  <span className={`px-1.5 py-0.2 text-[10px] rounded-full font-bold uppercase tracking-wider ${
                    link.badge === 'App' ? 'bg-kiki-blue text-white' :
                    link.badge === 'New' ? 'bg-kiki-coral text-white' :
                    'bg-slate-200 text-slate-700'
                  }`}>
                    {link.badge}
                  </span>
                )}
              </a>
            ))}
          </nav>

          {/* Right Action Controls */}
          <div className="flex items-center gap-2 sm:gap-2.5">
            {/* Music/Sound Toggle */}
            <button
              onClick={handleAudioToggle}
              title={isMuted ? 'Play Music & Sounds' : 'Mute Music & Sounds'}
              className={`p-2 sm:px-3 sm:py-2 rounded-2xl border-2 transition-all flex items-center gap-1.5 font-heading text-xs font-bold ${
                isMuted
                  ? 'border-slate-200 bg-slate-50 text-slate-400 hover:bg-slate-100'
                  : 'border-kiki-green bg-kiki-mint text-emerald-800 shadow-xs animate-pulse-subtle'
              }`}
            >
              {isMuted ? <VolumeOffIcon className="w-4 h-4 sm:w-5 sm:h-5" /> : <VolumeUpIcon className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-700" />}
              <span className="hidden sm:inline">{isMuted ? 'Muted' : 'Music On'}</span>
            </button>

            {/* Parent Gate Button */}
            <button
              onClick={() => {
                sounds.playSound('tap');
                onOpenParentGate();
              }}
              className="px-3 sm:px-3.5 py-2 rounded-2xl border-2 border-slate-200 hover:border-kiki-purple bg-white hover:bg-kiki-lavender/30 text-slate-700 hover:text-kiki-purple font-heading text-xs font-bold flex items-center gap-1.5 transition-all shadow-xs"
            >
              <LockIcon className="w-4 h-4 text-kiki-purple" />
              <span>Parents</span>
            </button>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50"
              aria-label="Toggle Navigation Menu"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-b-2 border-kiki-sky px-4 pt-3 pb-6 space-y-2 animate-pop">
          {navLinks.map((link) => (
            <a
              key={link.id}
              href={link.href}
              onClick={() => handleLinkClick(link)}
              className="block px-4 py-3 rounded-2xl font-heading text-base font-semibold text-slate-800 hover:bg-kiki-sky/40 flex items-center justify-between"
            >
              <span>{link.label}</span>
              {link.badge && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600 font-bold">
                  {link.badge}
                </span>
              )}
            </a>
          ))}
        </div>
      )}
    </header>
  );
}
